import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getTags, destroyTag } from '../../../api/TagsAPI';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../common/PerPageBlock';
import TagsReport from './TagsReport';

const TagsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [tagsList, setTagsList] = useState({ data: [] });
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [saveCompleted, setSaveCompleted] = useState(false);

    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            // Clear the message from location state to prevent it from showing again on refresh
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Tags';
        const filters = {
            paginate: true,
            page: currentPage,
            per_page: perPage,
            search_term: searchTermCompleted,
        };

        setViewDataLoading(true);
        getTags(filters, (response) => {
            setViewDataLoading(false);
            if (response.tags) {
                setTagsList(response.tags);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, saveCompleted]);

    const handleEditClick = (tagId) => {
        navigate(`/docs/tags/edit/${tagId}`);
    };

    const handleDeleteClick = (tag) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the tag: <strong>${tag.name}</strong></p><p>This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyTag(tag, (response) => {
                    if (response.tag) {
                        Swal.fire('Deleted!', 'The tag has been deleted.', 'success');
                        setSaveCompleted(prev => !prev);
                    } else {
                        Swal.fire('Error!', 'Could not delete the tag.', 'error');
                    }
                });
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title="Tags List">
                        <li><a className="f-s-14 f-w-500" href="#">Docs Master</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Tags</a></li>
                    </AppBreadCrumb>
                    <div className="row">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Tags</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row align-items-center">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-primary btn-sm m-md-3 fw-semibold f-s-14" type="button" onClick={() => navigate('/docs/tags/create')}>
                                                <i className="ti ti-plus f-s-18"></i> Add Tag
                                            </button>
                                            <TagsReport reportLoading={reportLoading} setReportLoading={setReportLoading} />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper">
                                <div className="d-flex m-md-3">
                                    {tagsList.data && tagsList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName="Tags" currentPage={tagsList.current_page} totalPages={tagsList.last_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    ) : ''}
                                    {(viewDataLoading || reportLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && tagsList.data && tagsList.data.length > 0 ? (
                                            tagsList.data.map((tag, index) => (
                                                <div className="col-md-6 col-xl-4 project-card" key={tag.id}>
                                                    <div className="card hover-effect">
                                                        <div className="card-header">
                                                            <div className="d-flex align-items-center">
                                                                <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden bg-primary text-white me-2">
                                                                    <i className="ti ti-tag f-s-18" />
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <h6 className="m-0 text-dark f-w-600">{tag.name}</h6>
                                                                </div>
                                                                <div className="dropdown">
                                                                    <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button">
                                                                        <i className="ti ti-dots-vertical text-dark" />
                                                                    </button>
                                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                                        <li>
                                                                            <a className="dropdown-item" onClick={() => handleEditClick(tag.id)}>
                                                                                <i className="ti ti-edit text-success" /> Edit
                                                                            </a>
                                                                        </li>
                                                                        <li>
                                                                            <a className="dropdown-item delete-button" onClick={() => handleDeleteClick(tag)}>
                                                                                <i className="ti ti-trash text-danger" /> Delete
                                                                            </a>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="d-flex">
                                                                <div>
                                                                    <h6 className="text-dark f-s-14">Name: <span className="text-success">{tag.name}</span></h6>
                                                                    <h6 className="text-dark f-s-14">Slug: <span className="text-danger">{tag.slug}</span></h6>
                                                                </div>
                                                                <div className="flex-grow-1 text-end">
                                                                    <p className="f-w-500 text-secondary">Id</p>
                                                                    <h6 className="f-w-600">{tag.id}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            !viewDataLoading && (
                                                <div className="col-12">
                                                     <div className="card mb-3 hover-effect">
                                                        <div className="row g-0">
                                                            <div className="col-md-8">
                                                                <div className="card-body">
                                                                    <h5 className="card-title">No Tags Found</h5>
                                                                    <p className="card-text">No records were found matching your criteria. Please try again or create a new tag.</p>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <img alt="No data" className="img-fluid" src="../assets/images/blog-app/09.jpg" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default TagsPage;

